// Story 1.32 — TanStack Query mutation:
// POST /api/v1/admin/financial-tools/manual-refund.
//
// Bypasses the Story 1.18 14-day window + amount-vs-refundable
// precondition (the whole reason this tool exists). On success the
// affected-user email + admin-log email are fired in tx2 by the
// backend.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ManualRefundInput = {
  user_id: string;
  source_ledger_entry_id: string;
  amount_cents: number;
  justification: string;
  /** Caller-supplied UUID (one per attempt). */
  idempotency_key: string;
};

export type ManualRefundResponse = {
  data: {
    refund_request_id: string;
    baas_refund_ref: string;
    amount_cents: number;
    expected_arrival_iso: string | null;
    card_last4: string;
    new_balance_cents: number;
  };
};

export class ManualRefundError extends Error {
  code?: string;
  field?: string;
  reason?: string;
  ledger_entry_id?: string;

  constructor(message: string) {
    super(message);
    this.name = "ManualRefundError";
  }
}

async function manualRefund(input: ManualRefundInput): Promise<ManualRefundResponse> {
  const { idempotency_key, ...body } = input;
  const response = await fetch("/api/v1/admin/financial-tools/manual-refund", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotency_key,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Manual refund failed (HTTP ${response.status})`;
    let parsed: Record<string, unknown> | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      parsed = raw?.error;
    } catch {
      // body not JSON
    }
    const err = new ManualRefundError(message);
    err.code = parsed?.code as string | undefined;
    err.field = parsed?.field as string | undefined;
    err.reason = parsed?.reason as string | undefined;
    err.ledger_entry_id = parsed?.ledger_entry_id as string | undefined;
    throw err;
  }
  return (await response.json()) as ManualRefundResponse;
}

export function useExecuteManualRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: manualRefund,
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: ["admin-financial-tools-refundable-topups", variables.user_id],
      });
    },
  });
}
